import { useState } from "react";
import {
  SparklesIcon,
  CircleStackIcon,
  ServerIcon,
  PencilIcon,
} from "@heroicons/react/24/outline";
import { Button } from "@/components";
import { toast } from "react-hot-toast";
import {
  useGeminiMutations,
  useProjectMutations,
} from "../../../../hooks/data/useProjectMutations";
import { geminiService } from "../../../../services/geminiService";
import type {
  Project,
  ProjectSchema,
  DataModel,
  ApiEndpoint,
  GenerateSchemaRequest,
} from "@/features/Projects/models/project.types";
import {
  ModelEditor,
  EndpointEditor,
  EmptyState,
  SchemaOverview,
  SchemaTabs,
} from "./components";
import { AIOperationFeedback } from '@/features/Projects/components/shared';
import { useAIOperationState } from "../../../../hooks/useAIOperationState";

interface SchemaPhaseProps {
  project: Project;
  onSave?: (schema: ProjectSchema) => Promise<void>;
  onNext?: () => void;
}

export default function SchemaPhase({ project, onSave, onNext }: SchemaPhaseProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editingModel, setEditingModel] = useState<
    DataModel | null | undefined
  >(undefined);
  const [editingEndpoint, setEditingEndpoint] = useState<
    ApiEndpoint | null | undefined
  >(undefined);
  const [showModelDeleteConfirm, setShowModelDeleteConfirm] = useState<
    string | null
  >(null);
  const [showEndpointDeleteConfirm, setShowEndpointDeleteConfirm] = useState<
    string | null
  >(null);
  const [isGeneratingModels, setIsGeneratingModels] = useState(false);
  const [isGeneratingEndpoints, setIsGeneratingEndpoints] = useState(false);

  const hasSchema = !!project.schema;
  const hasSchemaOrEditing = hasSchema || isEditing;

  // Use Gemini mutations hook
  const { generateSchema, isGeneratingSchema } = useGeminiMutations();

  // AI Operation State for consistent feedback
  const aiOperationState = useAIOperationState({
    operation: "Gerando Schema Técnico",
    canCancel: false,
    canRetry: true,
    canContinueInBackground: false,
    estimatedTime: 120 // 2 minutos estimado
  });

  const isAnyGenerating =
    isGeneratingSchema || isGeneratingModels || isGeneratingEndpoints;
  
  // Helper para verificar se está processando
  const isProcessingAI = ['processing', 'preparing', 'finalizing'].includes(aiOperationState.state.status);
 
  // Use project mutations hook for updates
  const { update: updateProjectMutation } = useProjectMutations();

  // Handlers
  const handleGenerateWithAI = () => {
    const request: GenerateSchemaRequest = {
      projectId: project.id,
      projectDescription: project.description,
      objectives: project.objectives || [],
      technicalSolution: project.technicalSolution,
      timeline: project.timeline,
      stories: project.stories,
      includeDocumentation: true,
      databasePreference: "postgresql",
      apiStyle: "rest",
      authRequired: true,
    };

    // Iniciar estado de operação de IA
    aiOperationState.startOperation();
    aiOperationState.setCurrentStep("Preparando solicitação para IA...");

    generateSchema.mutate(request, {
      onSuccess: (response) => {
        aiOperationState.completeOperation();
        toast.success("Schema gerado com sucesso!");
      },
      onError: (error) => {
        aiOperationState.errorOperation(
          error instanceof Error ? error.message : "Erro ao gerar schema"
        );
        console.error("Erro ao gerar schema:", error);
      }
    });
  };

  const handleRetryGeneration = () => {
    aiOperationState.resetOperation();
    handleGenerateWithAI();
  };

  const handleCreateManually = () => {
    const currentTime = new Date().toISOString();

    const emptySchema: ProjectSchema = {
      dataModels: [],
      apiEndpoints: [],
      apiGroups: [],
      documentation: [],
      config: {
        apiVersion: "v1",
        baseUrl: "https://api.exemplo.com",
        databaseType: "postgresql",
        authenticationMethod: "jwt",
        corsEnabled: true,
        rateLimitEnabled: true,
        loggingEnabled: true,
        openApiInfo: {
          title: `${project.name} API`,
          description: project.description.substring(0, 200),
          version: "1.0.0",
        },
      },
      stats: {
        totalModels: 0,
        totalEndpoints: 0,
        totalFields: 0,
        complexity: "medium",
      },
      createdAt: currentTime,
      updatedAt: currentTime,
    };

    setIsEditing(true);
    updateProjectMutation({
      id: project.id,
      data: {
        schema: emptySchema,
        currentPhase: "schema" as const,
      },
    });
  };

  const handleAddModel = () => {
    setEditingModel(null); // null significa novo modelo
  };

  const handleEditModel = (model: DataModel) => {
    setEditingModel(model);
  };

  const handleSaveModel = (model: DataModel) => {
    if (!project.schema) return;

    let updatedModels: DataModel[];
    const existingIndex = project.schema.dataModels.findIndex(
      (m) => m.id === model.id
    );

    if (existingIndex >= 0) {
      // Editando modelo existente
      updatedModels = [...project.schema.dataModels];
      updatedModels[existingIndex] = model;
      toast.success("Modelo atualizado com sucesso!");
    } else {
      // Novo modelo
      updatedModels = [...project.schema.dataModels, model];
      toast.success("Modelo criado com sucesso!");
    }

    const totalFields = updatedModels.reduce(
      (total, model) => total + model.fields.length,
      0
    );

    const updatedSchema = {
      ...project.schema,
      dataModels: updatedModels,
      stats: {
        ...project.schema.stats,
        totalModels: updatedModels.length,
        totalFields,
      },
    };

    updateProjectMutation({
      id: project.id,
      data: { schema: updatedSchema },
    });

    setEditingModel(undefined);
  };

  const handleCancelEditModel = () => {
    setEditingModel(undefined);
  };

  const handleAddEndpoint = () => {
    setEditingEndpoint(null); // null significa novo endpoint
  };

  const handleEditEndpoint = (endpoint: ApiEndpoint) => {
    setEditingEndpoint(endpoint);
  };

  const handleSaveEndpoint = (endpoint: ApiEndpoint) => {
    if (!project.schema) return;

    let updatedEndpoints: ApiEndpoint[];
    const existingIndex = project.schema.apiEndpoints.findIndex(
      (e) => e.id === endpoint.id
    );

    if (existingIndex >= 0) {
      // Editando endpoint existente
      updatedEndpoints = [...project.schema.apiEndpoints];
      updatedEndpoints[existingIndex] = endpoint;
      toast.success("Endpoint atualizado com sucesso!");
    } else {
      // Novo endpoint
      updatedEndpoints = [...project.schema.apiEndpoints, endpoint];
      toast.success("Endpoint criado com sucesso!");
    }

    const updatedSchema = {
      ...project.schema,
      apiEndpoints: updatedEndpoints,
      stats: {
        ...project.schema.stats,
        totalEndpoints: updatedEndpoints.length,
      },
    };

    updateProjectMutation({
      id: project.id,
      data: { schema: updatedSchema },
    });

    setEditingEndpoint(undefined);
  };

  const handleCancelEditEndpoint = () => {
    setEditingEndpoint(undefined);
  };

  const handleGenerateModelsWithAI = async () => {
    if (!project.schema) return;

    setIsGeneratingModels(true);
    try {
      // Usar apenas a parte de geração de modelos do geminiService
      const response = await geminiService.generateDataModels({
        projectDescription: project.description,
        objectives: project.objectives || [],
        technicalContext: project.technicalSolution?.description || "",
        storiesContext: JSON.stringify(project.stories),
        databasePreference: "postgresql",
      });

      const totalFields = response.reduce(
        (total: number, model: any) => total + model.fields.length,
        0
      );

      const updatedSchema = {
        ...project.schema,
        dataModels: response,
        stats: {
          ...project.schema.stats,
          totalModels: response.length,
          totalFields,
        },
      };

      updateProjectMutation({
        id: project.id,
        data: { schema: updatedSchema },
      });

      toast.success("Modelos de dados gerados com sucesso!");
    } catch (error) {
      console.error("Erro ao gerar modelos:", error);
      toast.error("Erro ao gerar modelos de dados");
    } finally {
      setIsGeneratingModels(false);
    }
  };

  const handleGenerateEndpointsWithAI = async () => {
    if (!project.schema || project.schema.dataModels.length === 0) return;

    setIsGeneratingEndpoints(true);
    try {
      // Usar apenas a parte de geração de endpoints do geminiService
      const response = await geminiService.generateApiEndpoints({
        projectDescription: project.description,
        objectives: project.objectives || [],
        dataModels: project.schema.dataModels,
        apiStyle: "rest",
        authRequired: true,
        // 🎯 CONTEXTO ENRIQUECIDO - Incluindo todas as informações do projeto
        technicalSolution: project.technicalSolution,
        stories: project.stories,
        timeline: project.timeline,
      });

      const updatedSchema = {
        ...project.schema,
        apiEndpoints: response,
        stats: {
          ...project.schema.stats,
          totalEndpoints: response.length,
        },
      };

      updateProjectMutation({
        id: project.id,
        data: { schema: updatedSchema },
      });

      toast.success("Endpoints de API gerados com sucesso!");
    } catch (error) {
      console.error("Erro ao gerar endpoints:", error);
      toast.error("Erro ao gerar endpoints de API");
    } finally {
      setIsGeneratingEndpoints(false);
    }
  };

  const handleDeleteModel = (modelId: string) => {
    if (!project.schema) return;

    const updatedModels = project.schema.dataModels.filter(
      (m) => m.id !== modelId
    );

    const totalFields = updatedModels.reduce(
      (total, model) => total + model.fields.length,
      0
    );

    const updatedSchema = {
      ...project.schema,
      dataModels: updatedModels,
      stats: {
        ...project.schema.stats,
        totalModels: updatedModels.length,
        totalFields,
      },
    };

    updateProjectMutation({
      id: project.id,
      data: { schema: updatedSchema },
    });

    setShowModelDeleteConfirm(null);
    toast.success("Modelo removido com sucesso!");
  };

  const handleDeleteEndpoint = (endpointId: string) => {
    if (!project.schema) return;

    const updatedEndpoints = project.schema.apiEndpoints.filter(
      (e) => e.id !== endpointId
    );

    const updatedSchema = {
      ...project.schema,
      apiEndpoints: updatedEndpoints,
      stats: {
        ...project.schema.stats,
        totalEndpoints: updatedEndpoints.length,
      },
    };

    updateProjectMutation({
      id: project.id,
      data: { schema: updatedSchema },
    });

    setShowEndpointDeleteConfirm(null);
    toast.success("Endpoint removido com sucesso!");
  };

  // Se não tem schema e não está editando, mostra opções de criação
  if (!hasSchemaOrEditing) {
    return (
      <div className="space-y-6">
        {/* Feedback de operação de IA */}
        {(aiOperationState.state.status !== 'idle') && (
          <AIOperationFeedback
            operation={aiOperationState.operation}
            state={aiOperationState.state}
            onRetry={handleRetryGeneration}
            onDismiss={aiOperationState.resetOperation}
          />
        )}
        
        <EmptyState
          onGenerateWithAI={handleGenerateWithAI}
          onCreateManually={handleCreateManually}
          isGeneratingSchema={isProcessingAI}
        />
      </div>
    );
  }

  // Renderizar schema existente
  return (
    <div className="space-y-6">
      {/* Feedback de operação de IA */}
      {(aiOperationState.state.status !== 'idle') && (
        <AIOperationFeedback
          operation={aiOperationState.operation}
          state={aiOperationState.state}
          onRetry={handleRetryGeneration}
          onDismiss={aiOperationState.resetOperation}
        />
      )}
      
      {/* Header com estatísticas */}
      <SchemaOverview schema={project.schema} />

      {/* Ações */}
      <div className="flex flex-wrap gap-3">
        <Button
          onClick={handleGenerateWithAI}
          disabled={isProcessingAI}
          leftIcon={<SparklesIcon className="h-4 w-4" />}
          size="sm"
          className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
        >
          {isProcessingAI ? "Regenerando..." : "Regenerar com IA"}
        </Button>

        <Button
          variant="outline"
          onClick={handleAddModel}
          leftIcon={<CircleStackIcon className="h-4 w-4" />}
          size="sm"
        >
          Novo Modelo
        </Button>

        <Button
          variant="outline"
          onClick={handleAddEndpoint}
          leftIcon={<ServerIcon className="h-4 w-4" />}
          size="sm"
        >
          Novo Endpoint
        </Button>
      </div>

      {/* Componente de edição de modelo */}
      {editingModel !== undefined && (
        <ModelEditor
          model={editingModel}
          onSave={handleSaveModel}
          onCancel={handleCancelEditModel}
        />
      )}

      {editingEndpoint !== undefined && (
        <EndpointEditor
          endpoint={editingEndpoint}
          onSave={handleSaveEndpoint}
          onCancel={handleCancelEditEndpoint}
        />
      )}

      {project.schema && (
        <>
          {/* Banner de status de geração */}
          {isAnyGenerating && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-center space-x-3">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                <div>
                  <p className="text-sm font-medium text-blue-800">
                    {isGeneratingModels && "Gerando modelos de dados com IA..."}
                    {isGeneratingEndpoints &&
                      "Gerando endpoints de API com IA..."}
                    {isGeneratingSchema && "Gerando schema completo com IA..."}
                  </p>
                  <p className="text-xs text-blue-600">
                    Esta operação pode levar alguns segundos. Por favor,
                    aguarde.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Sistema de Abas para melhorar a UX */}
          <SchemaTabs
            schema={project.schema}
            onGenerateModelsWithAI={handleGenerateModelsWithAI}
            onAddModel={handleAddModel}
            onEditModel={handleEditModel}
            onDeleteModel={handleDeleteModel}
            showModelDeleteConfirm={showModelDeleteConfirm}
            setShowModelDeleteConfirm={setShowModelDeleteConfirm}
            onGenerateEndpointsWithAI={handleGenerateEndpointsWithAI}
            onAddEndpoint={handleAddEndpoint}
            onEditEndpoint={handleEditEndpoint}
            onDeleteEndpoint={handleDeleteEndpoint}
            showEndpointDeleteConfirm={showEndpointDeleteConfirm}
            setShowEndpointDeleteConfirm={setShowEndpointDeleteConfirm}
            isGeneratingModels={isGeneratingModels}
            isGeneratingEndpoints={isGeneratingEndpoints}
            isAnyGenerating={isAnyGenerating}
            isEditing={isEditing}
          />
        </>
      )}
    </div>
  );
}